import { useState } from 'react';
import type {
  PreviewRoutingConfigSkuCoverageFillResponse,
  RoutingConfigSkuCoverageFillDisposition,
  RoutingConfigSkuCoverageFillResult,
  RoutingConfigSkuCoverageTargetIdentity,
  RoutingSkuTargetRecord,
} from '@nadoutong/contracts';
import {
  useApplyRoutingConfigSkuCoverageFill,
  usePreviewRoutingConfigSkuCoverageFill,
} from '../../api/useTenantRouting.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useT, type I18nKey } from '../../i18n/index.ts';
import { Button, Chip, Dialog, FormField, Select, TBody, Table, Td, Th, THead, Tr } from '../../ui/index.ts';
import type { SkuOption } from '../tenantRoutingShared.ts';

/**
 * Namespace coverage-fill preview/apply flow, lifted out of
 * `TenantRoutingConfigSheet` by #1493 so the sheet file stays under the
 * size budget. Behaviour is unchanged; the only structural difference is
 * that the caller now mounts this conditionally, so the preview/applied
 * bookkeeping below resets by unmounting instead of by four setState calls
 * scattered across the sheet's open/close/cancel paths.
 */

function coverageDispositionLabelKey(
  disposition: RoutingConfigSkuCoverageFillDisposition,
): I18nKey {
  if (disposition === 'add') return 'tenant_routing.coverage.add';
  if (disposition === 'conflict') return 'tenant_routing.coverage.conflict';
  return 'tenant_routing.coverage.skipped';
}

function coverageReasonLabelKey(
  reason: RoutingConfigSkuCoverageFillResult['reason'],
): I18nKey {
  if (reason === 'missing') return 'tenant_routing.coverage.reason_missing';
  if (reason === 'already_exists') return 'tenant_routing.coverage.reason_already_exists';
  if (reason === 'explicit_coverage_exists') {
    return 'tenant_routing.coverage.reason_explicit_coverage_exists';
  }
  return 'tenant_routing.coverage.reason_capability_not_live';
}

interface Props {
  readonly configId: string;
  readonly configName: string;
  readonly assignedOrgCount: number;
  readonly channelSlug: string;
  /** SKUs of the chosen namespace with neither an explicit target nor a policy. */
  readonly missingSkus: readonly SkuOption[];
  /** Capability-pinned rows already in this config's pools, keyed for the Select. */
  readonly availableIdentities: ReadonlyMap<string, RoutingSkuTargetRecord>;
  readonly onClose: () => void;
}

export function TenantRoutingCoverageFillDialog({
  configId,
  configName,
  assignedOrgCount,
  channelSlug,
  missingSkus,
  availableIdentities,
  onClose,
}: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const previewCoverage = usePreviewRoutingConfigSkuCoverageFill();
  const applyCoverage = useApplyRoutingConfigSkuCoverageFill();
  const [identityKey, setIdentityKey] = useState<string | undefined>(
    () => availableIdentities.keys().next().value,
  );
  const [preview, setPreview] = useState<PreviewRoutingConfigSkuCoverageFillResponse | null>(null);
  const [applied, setApplied] = useState(false);

  async function handlePreview(): Promise<void> {
    if (identityKey === undefined) return;
    const target = availableIdentities.get(identityKey);
    if (target === undefined || target.capability_id === null) return;
    const identity: RoutingConfigSkuCoverageTargetIdentity = {
      capability_id: target.capability_id,
      upstream_id: target.upstream_id,
      account_id: target.account_id,
      key_bucket_id: target.key_bucket_id,
      upstream_channel_id: target.upstream_channel_id,
    };
    try {
      const response = await previewCoverage.mutateAsync({
        items: missingSkus.map((sku) => ({
          config_id: configId,
          sku_id: sku.skuId,
          sku_name: sku.skuName,
          vendor: sku.vendor,
          model: sku.model,
          target: identity,
        })),
      });
      setPreview(response);
      setApplied(false);
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  async function handleApply(): Promise<void> {
    if (preview === null) return;
    try {
      const response = await applyCoverage.mutateAsync({ items: preview.results.map((row) => ({
        config_id: row.config_id, sku_id: row.sku_id, sku_name: row.sku_name,
        vendor: row.vendor, model: row.model, target: row.target,
      })) });
      toast.success(t('tenant_routing.coverage.applied', { count: String(response.inserted) }));
      setPreview(response);
      setApplied(true);
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={t('tenant_routing.coverage.preview_title')}
      description={`${channelSlug} · ${configName}`}
      closeLabel={t('common.close')}
      size="lg"
      testId="tenant-routing-coverage-preview-dialog"
      footer={<>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        {preview === null ? (
          <Button variant="primary" disabled={identityKey === undefined || previewCoverage.isPending || missingSkus.length === 0} onClick={() => { void handlePreview(); }}>{t('tenant_routing.coverage.preview')}</Button>
        ) : (
          <Button variant="primary" disabled={applied || preview.counts.add === 0 || applyCoverage.isPending} onClick={() => { void handleApply(); }}>{t('tenant_routing.coverage.apply')}</Button>
        )}
      </>}
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-2 rounded-md bg-well p-3 text-dense">
          <span>{t('tenant_routing.coverage.affected_customers', {
            count: String(
              preview?.impacts.find((impact) => impact.config_id === configId)
                ?.affected_assigned_org_count ?? assignedOrgCount,
            ),
          })}</span>
          <span className="text-paper-low">{t('tenant_routing.coverage.missing_list', { skus: missingSkus.map((sku) => sku.skuName).join(', ') })}</span>
        </div>
        <FormField
          label={t('tenant_routing.coverage.identity_label')}
          fieldId="tenant-routing-coverage-identity"
        >
          <Select
            id="tenant-routing-coverage-identity"
            ariaLabel={t('tenant_routing.coverage.identity_label')}
            value={identityKey}
            onValueChange={(value) => { setIdentityKey(value); setPreview(null); setApplied(false); }}
            options={[...availableIdentities].map(([value, target]) => ({
              value,
              label: `${target.sku_name} → ${target.upstream_model_name} / ${target.capability_id} / ${target.upstream_id} / ${target.account_id} / ${target.key_bucket_id} / ${target.upstream_channel_slug}`,
            }))}
            placeholder={t('tenant_routing.coverage.select_upstream')}
            mono
          />
        </FormField>
        <p className="m-0 text-dense text-paper-low">{t('tenant_routing.coverage.non_overwrite')}</p>
        {preview === null ? null : <div className="flex flex-col gap-3" data-testid="tenant-routing-coverage-preview-results">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded bg-well p-3"><span className="block text-micro text-paper-low">{t('tenant_routing.coverage.add')}</span><span className="font-mono text-title text-ok">{preview.counts.add}</span></div>
            <div className="rounded bg-well p-3"><span className="block text-micro text-paper-low">{t('tenant_routing.coverage.conflict')}</span><span className="font-mono text-title text-warn">{preview.counts.conflict}</span></div>
            <div className="rounded bg-well p-3"><span className="block text-micro text-paper-low">{t('tenant_routing.coverage.skipped')}</span><span className="font-mono text-title text-paper">{preview.counts.skipped}</span></div>
          </div>
          <Table testId="tenant-routing-coverage-preview-table">
            <THead><Tr interactive={false}>
              <Th>{t('tenant_routing.coverage.column_sku')}</Th>
              <Th>{t('tenant_routing.coverage.column_outcome')}</Th>
              <Th>{t('tenant_routing.coverage.column_reason')}</Th>
              <Th>{t('tenant_routing.coverage.column_identity')}</Th>
              <Th>{t('tenant_routing.coverage.column_mode')}</Th>
            </Tr></THead>
            <TBody>
              {preview.results.map((row) => {
                const coverageMode = preview.impacts.find(
                  (impact) => impact.config_id === row.config_id,
                )?.coverage_mode ?? 'exclusive';
                return <Tr key={[row.config_id, row.sku_id, row.target.capability_id, row.target.upstream_id, row.target.account_id, row.target.key_bucket_id, row.target.upstream_channel_id].join(':')}>
                  <Td>
                    <span className="block text-paper">{row.sku_name}</span>
                    <span className="block font-mono text-micro text-paper-low">{row.sku_id}</span>
                  </Td>
                  <Td><Chip tone={row.disposition === 'add' ? 'ok' : row.disposition === 'conflict' ? 'warn' : 'neutral'}>{t(coverageDispositionLabelKey(row.disposition))}</Chip></Td>
                  <Td>{t(coverageReasonLabelKey(row.reason))}</Td>
                  <Td mono className="min-w-64 break-all text-micro">
                    <span className="block text-paper">{row.target.upstream_model_name ?? '—'} / {row.target.capability_id}</span>
                    <span className="block text-paper-low">{row.target.capability_protocol ?? '—'}</span>
                    <span className="block">{row.target.upstream_id} / {row.target.account_id}</span>
                    <span className="block text-paper-low">{row.target.key_bucket_id} / {row.target.upstream_channel_id}</span>
                  </Td>
                  <Td>{t(coverageMode === 'inherit' ? 'tenant_routing.coverage.mode_inherit' : 'tenant_routing.coverage.mode_exclusive')}</Td>
                </Tr>;
              })}
            </TBody>
          </Table>
        </div>}
      </div>
    </Dialog>
  );
}
